'use client'

import { forwardRef, memo, useCallback, useImperativeHandle, useRef, type ReactNode } from 'react'

import { cn } from '../lib/cn'

export interface MarqueeTextProps {
  children: ReactNode
  className?: string
  speed?: number
}

const MarqueeTextComponent = forwardRef<HTMLSpanElement, MarqueeTextProps>(
  ({ children, className, speed = 60 }, ref) => {
    const containerRef = useRef<HTMLSpanElement>(null)
    const textRef = useRef<HTMLSpanElement>(null)
    const effectiveSpeed = speed && speed > 0 ? speed : 60

    useImperativeHandle(ref, () => containerRef.current as HTMLSpanElement)

    const startScroll = useCallback(() => {
      const container = containerRef.current
      const text = textRef.current
      if (!container || !text) return

      const overflow = text.scrollWidth - container.clientWidth
      if (overflow <= 0) return

      text.style.transition = `transform ${(overflow / effectiveSpeed) * 1000}ms linear`
      text.style.transform = `translateX(-${overflow}px)`
      text.classList.remove('truncate')
    }, [effectiveSpeed])

    const stopScroll = useCallback(() => {
      const text = textRef.current
      if (!text) return

      text.style.transition = 'transform 150ms ease-out'
      text.style.transform = ''
    }, [])

    const handleTransitionEnd = useCallback(() => {
      const text = textRef.current
      if (!text || text.style.transform !== '') return

      text.style.transition = ''
      text.classList.add('truncate')
    }, [])

    return (
      <span
        className={cn('block overflow-hidden', className)}
        ref={containerRef}
        onMouseEnter={startScroll}
        onMouseLeave={stopScroll}
      >
        <span ref={textRef} className='block whitespace-nowrap truncate' onTransitionEnd={handleTransitionEnd}>
          {children}
        </span>
      </span>
    )
  },
)

MarqueeTextComponent.displayName = 'MarqueeText'

export const MarqueeText = memo(MarqueeTextComponent)

MarqueeText.displayName = 'MarqueeText'
